import { useState, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { format } from "date-fns";
import { useTheme, useMediaQuery, Box, 
    FormControl, MenuItem, InputLabel, Select,       
} from '@mui/material';
import useAuthCheck from 'hooks/useAuthCheck';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import { LAYOUT_HEIGHT } from 'state/constants';
import { countRouteBlockHours } from '../../charts/utils';
import Header from 'components/Header';
import ScrollToTop from 'components/ScrollToTop';
import FlexBetween from 'components/FlexBetween';
import FilterButton from 'components/FilterButton';
import ChartDialog from 'components/chartNivo/ChartDialog';
import RouteFilterDialog from 'components/RouteFilterDialog';
import SortBarMultiLinesChart from 'components/chartNivo/SortBarMultiLinesChart';
import { boxStyles as styles } from 'components/CustomStyles';


const viewOptions = [
  { value: "bhByBase", label: "by Base" },
  { value: "bhByRank", label: "by Rank" },
];

const CrewBlockHoursChart = () => {    
    useAuthCheck();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

    const { startDate, endDate } = useDateRangeContext();
    
    const scrollRef = useRef(null);
    const [chartOpen, setChartOpen] = useState(false); 
    const [selectedChart, setSelectedChart] = useState(null);
    const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
    const [filters, setFilters] = useState(null);
    
    const [view, setView] = useState('bhByBase');
    const [data, setData] = useState({
        bhByBase: [],
        bhByRank: [],       
    });

    const crewRanksData = useSelector((state) => state.crewRanks);
    const crewRoutesData = useSelector((state) => state.job255CrewRoutes);    
       
    const filteredCrewRoutes = useMemo(() => {
        if (!Array.isArray(crewRoutesData)) {
            return [];
        }
    
        if (!filters) return Object.values(crewRoutesData);
    
        return Object.values(crewRoutesData).filter((route) => {
            return Object.entries(filters).every(([key, value]) => {
                if (!value || (Array.isArray(value) && value.length === 0)) return true;
    
                if (key === 'routeDate') {
                    const formattedRouteDate = route[key] ? format(new Date(route[key]), 'yyyy-MM-dd') : null;
                    const formattedFilterDate = value ? format(new Date(value), 'yyyy-MM-dd') : null;
                    return formattedRouteDate === formattedFilterDate;
                }
    
                // Handle multiple selections for AC Type and Flight Reg
                if (key === 'acType' || key === 'flightReg') {
                    return Array.isArray(value) ? value.includes(route[key]) : route[key] === value;
                }
    
                return route[key] === value;
            });
        });
    }, [crewRoutesData, filters]);       
    
    useEffect(() => {  
        const results = {
            bhByBase: countRouteBlockHours(filteredCrewRoutes, crewRanksData, 'base'),
            bhByRank: countRouteBlockHours(filteredCrewRoutes, crewRanksData, 'post'), 
        };
        
        setData(results); 
    }, [filteredCrewRoutes, crewRanksData]); 
        
    const handleFilterOpen = () => setIsFilterDialogOpen(true);
    const handleFilterClose = () => setIsFilterDialogOpen(false);
    
    const handleFilterApply = (filterValues) => {
        setFilters(filterValues); 
    };

    const buildOptions = (data, key) =>
        [...new Set(data.map(item => item[key]))].sort();
    
    const { 
        acTypeOptions, 
        flightRegOptions, 
        depOptions, 
        arrOptions 
    } = useMemo(() => ({
        acTypeOptions: buildOptions(crewRoutesData, 'acType'),
        flightRegOptions: buildOptions(crewRoutesData, 'flightReg'),
        depOptions: buildOptions(crewRoutesData, 'dep'),
        arrOptions: buildOptions(crewRoutesData, 'arr'),
    }), [crewRoutesData]);
  
    const handleChartOpen = (chart) => {
        setSelectedChart(
            <Box sx={{ ...styles }}>
                <Box 
                    height="100%" 
                    width={"100%"} 
                    sx={chart.style}
                >
                    {chart.component}
                </Box>                
            </Box>
        );
        setChartOpen(true);
    };

    const handleCloseDialog = () => {
        setSelectedChart(null);
        setChartOpen(false);
    };

    const period = `${startDate ? format(startDate, 'ddMMMyy') : ''} - ${endDate ? format(endDate, 'ddMMMyy') : ''}`;
    const defaultTitle = `Crew Block Hours ${period}`;

    const chartMap = {
        bhByBase: [
            { component: <SortBarMultiLinesChart data={data.bhByBase} title={defaultTitle} bottomLegend='Base' leftLegend='Block Hours' digit={1} barTranslateXOffSet={80} lineTranslateXOffSet={-10} />, style: {} },
        ],
        bhByRank: [
            { component: <SortBarMultiLinesChart data={data.bhByRank} title={defaultTitle} bottomLegend='Rank' leftLegend='Block Hours' isHorizontalBottomLegend={true} digit={1} bottomOffSet={-30} bottomLegendOffSet={-30} barTranslateXOffSet={80} lineTranslateXOffSet={-10} />, style: {} },
        ],  
    }; 

    const renderCharts = (charts, customStyles) => (
        <Box
            ref={scrollRef}
            height={LAYOUT_HEIGHT}  
            display="flex"
            flexDirection="column"
            gap={2}
            sx={{ gridColumn: "span 12", overflowY: 'auto' }}
        >
            {charts.map((chart, index) => (
                <Box
                    key={index}
                    onDoubleClick={() => handleChartOpen(chart)}
                    sx={{ ...styles, ...customStyles }}
                >
                    <Box 
                        height="100%" 
                        width={"100%"} 
                        sx={chart.style}
                    >
                        {chart.component}
                    </Box>
                </Box>
            ))}
        </Box>
    );

    
    return (
        <Box className="main-container-box">                  
            <FlexBetween>
                <Header 
                    title="Crew Block Hours (Job 255)" 
                    subtitle="List of Charts"
                /> 
                <Box display="flex" alignItems="center" gap={1}>
                    <FormControl
                        size="small"
                        sx={{ width: { xs: '8rem', sm: '9rem' } }}
                    >
                        <InputLabel id="bh-view-label">View</InputLabel>
                        <Select
                            labelId="bh-view-label"
                            label="View"
                            value={view}
                            onChange={(e) => setView(e.target.value)}
                        >
                            {viewOptions.map(opt => (
                                <MenuItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    
                    <FilterButton
                        filters={filters}
                        onClick={handleFilterOpen}
                    />
                </Box>
              
            </FlexBetween>
            <Box
                display="grid"                        
                gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                sx={{ "& > div": { gridColumn: isDesktop ? undefined : "span 12" } }}
            >
                {view in chartMap &&
                    renderCharts(chartMap[view], { minHeight: LAYOUT_HEIGHT })
                }

                <ScrollToTop scrollRef={scrollRef} />
            </Box>
               
            {chartOpen && (
                <ChartDialog
                    open={chartOpen}
                    onClose={handleCloseDialog}
                    chartComponent={selectedChart}
                />
            )}  

            {isFilterDialogOpen &&
                <RouteFilterDialog
                    open={isFilterDialogOpen}
                    onClose={handleFilterClose}
                    onApply={handleFilterApply}
                    acTypeOptions={acTypeOptions}
                    flightRegOptions={flightRegOptions}
                    depOptions={depOptions}
                    arrOptions={arrOptions}
                    currentFilters={filters}
                />
            }
        </Box>
    );
};

export default CrewBlockHoursChart;
